import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { Calendar, Send, CheckCircle } from 'lucide-react'
import { Container } from '../layout/Container'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'
import { leadsApi } from '../../lib/api'

export function FinalCTA() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const schema = z.object({
    name: z.string().min(2, t('finalCta.errors.name')),
    email: z.string().email(t('finalCta.errors.email')),
    company: z.string().optional(),
    role: z.string().optional(),
    message: z.string().optional(),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    try {
      await leadsApi.create(data)
      setIsSubmitted(true)
      reset()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('finalCta.errors.generic'))
    }
  }

  return (
    <section id="diagnostico" className="bg-surface py-24">
      <Container size="narrow">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 32 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-12 text-center">
            <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
              {t('finalCta.label')}
            </span>
            <h2 className="mt-4 text-3xl font-semibold text-text-primary md:text-4xl">
              {t('finalCta.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-text-secondary">
              {t('finalCta.description')}
            </p>
          </div>

          <Card variant="gradient">
            {isSubmitted ? (
              <div className="py-12 text-center">
                <CheckCircle className="mx-auto h-16 w-16 text-success" />
                <h3 className="mt-6 text-2xl font-semibold text-text-primary">
                  {t('finalCta.successTitle')}
                </h3>
                <p className="mt-2 text-text-secondary">{t('finalCta.successMessage')}</p>
                <Button asChild variant="outline" className="mt-6">
                  <a
                    href="https://calendly.com/empleahoy/diagnostico"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {t('finalCta.calendarLink')}
                  </a>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-text-primary">
                      {t('finalCta.fields.name')} *
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...register('name')}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      placeholder={t('finalCta.placeholders.name')}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-error">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-text-primary">
                      {t('finalCta.fields.email')} *
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...register('email')}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      placeholder={t('finalCta.placeholders.email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-error">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="company" className="mb-2 block text-sm font-medium text-text-primary">
                      {t('finalCta.fields.company')}
                    </label>
                    <input
                      id="company"
                      type="text"
                      {...register('company')}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      placeholder={t('finalCta.placeholders.company')}
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="mb-2 block text-sm font-medium text-text-primary">
                      {t('finalCta.fields.role')}
                    </label>
                    <input
                      id="role"
                      type="text"
                      {...register('role')}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      placeholder={t('finalCta.placeholders.role')}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-text-primary">
                    {t('finalCta.fields.message')}
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    {...register('message')}
                    className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent resize-none"
                    placeholder={t('finalCta.placeholders.message')}
                  />
                </div>

                {submitError && (
                  <p className="text-sm text-error">{submitError}</p>
                )}

                <Button type="submit" size="lg" isLoading={isSubmitting} className="w-full md:w-auto">
                  <Send className="mr-2 h-4 w-4" />
                  {t('finalCta.submit')}
                </Button>
              </form>
            )}
          </Card>
        </motion.div>
      </Container>
    </section>
  )
}
